"use client"

import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { Timer, RefreshCw, ChevronRight, Frown, CircleCheck } from "lucide-react"
import { handleAndTrackError } from "@/lib/helper"
import { fetchData } from "@/lib/helper"
import { startFexprimeKyc } from "@/lib/fexprimeKyc"
import config from "@/config"
import SupportRequestDialog from "@/components/common/SupportRequestDialog"
import PendingKyc from "@/assets/icons/banking/kyc/pending.svg"
import PendingBanking from "@/assets/icons/banking/emandate/pending.svg"
import PendingEsign from "@/assets/icons/banking/esign/pending.svg"
import RejectedKyc from "@/assets/icons/banking/kyc/rejected.svg"
import RejectedBanking from "@/assets/icons/banking/emandate/rejected.svg"
import RejectedESign from "@/assets/icons/banking/esign/rejected.svg"
import ProgressESign from "@/assets/icons/banking/esign/progress.svg"
import ProgressBanking from "@/assets/icons/banking/emandate/progress.svg"

export default function StepCard({ data, setIsLoading }) {
  const navigate = useNavigate()
  const [isClicked, setIsClicked] = useState(false)
  const partner = localStorage.getItem("partnerIdentifier")
  const [supportDialog, setSupportDialog] = useState({
    open: false,
    topic: null,
    problemLine: "",
  })
  const openSupportDialog = (topic, problemLine) =>
    setSupportDialog({ open: true, topic, problemLine })

  const startESign = async () => {
    setIsLoading((prevState) => !prevState)
    await fetchData("esign/start-esign")
    navigate(`/esign`)
    setIsLoading((prevState) => !prevState)
  }

  const clickKycAction = async () => {
    try {
      setIsLoading((prevState) => !prevState)

      const journeyResponse = await fetchData("/journey/full")
      console.log("[v0] Journey response:", journeyResponse)

      const { journey } = journeyResponse

      if (journey?.lender === "FEXPRIME") {
        // CKYC first (when the rollout flag is on), then the mandatory eKYC.
        // Both cards share one implementation — see src/lib/fexprimeKyc.js for why
        // there are two endpoints and why unknown answers fail OPEN to the eKYC.
        await startFexprimeKyc({ navigate, partner })
      } else {
        // Native/CKYC flow — mirrors OnboardingStepsCard.jsx's non-FEXPRIME branch exactly
        // (same endpoint, same event shapes, same `/kyc` confirm-page hand-off), since this
        // card previously never called it at all.
        const kycResponse = await fetch(`${config.API_URL}/ekyc/start-kyc/`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${localStorage.getItem(config.LOCAL_KEYS.SIGNUP_TOKEN)}`,
          },
        })

        if (!kycResponse.ok) {
          const errorData = await kycResponse.json().catch(() => ({}))
          handleAndTrackError(new Error(errorData.message || "Failed to start KYC session."), {
            apiPath: "/ekyc/start-kyc/",
            responseStatus: kycResponse.status,
            responseError: errorData,
          })
          return
        }

        const kycData = await kycResponse.json()
        if (
          kycData?.event === "CKYC_SEARCH_SUCCESS" &&
          kycData?.data?.sessionUrl === null
        ) {
          navigate(`/kyc?partner=${partner}`, {
            state: {
              data: kycData?.data?.result?.[0] || kycData?.data?.result,
              kycRefId: kycData?.data?.refId,
            },
          })
          return
        } else if (kycData?.event === "CKYC_SEARCH_SUCCESS" && kycData?.data?.sessionUrl) {
          navigate(`/kyc?partner=${partner}`, {
            state: {
              data: kycData?.data?.result?.[0] || kycData?.data?.result,
              kycRefId: kycData?.data?.refId,
            },
          })
          return
        } else if (
          kycData?.event === "FULL_KYC_INITIATED" ||
          kycData?.event === "CKYC_DOWNLOAD_SUCCESS"
        ) {
          const sessionUrl = kycData?.data?.sessionUrl
          if (!sessionUrl) {
            handleAndTrackError(
              new Error(`KYC session URL missing for event ${kycData?.event}`),
              { apiPath: "/ekyc/start-kyc/", kycEvent: kycData?.event }
            )
            return
          }
          window.location.href = sessionUrl
          return
        }
      }
    } catch (error) {
      handleAndTrackError(error, {
        apiPath: "journey/full",
      })
    } finally {
      setIsLoading((prevState) => !prevState)
    }
  }

  if (!data) return null

  const cardConfig = {
    KYC: {
      heading: "Identity Verification",
      PENDING: {
        UNLOCKED: {
          color: "#0031CC",
          message: "Upload your PAN and Selfie to verify your identity",
          action: {
            text: "Start",
            className: `${
              !isClicked ? "bg-white" : "bg-gray-500"
            } text-black text-2xs px-2 py-0.5 rounded-md flex gap-1 items-center`,
            icon: (
              <div className="rounded-full bg-black">
                <ChevronRight color="#000" size={12} />
              </div>
            ),
            onClick: () => {
              setIsClicked((prevState) => !prevState)
              clickKycAction()
            },
          },
          showTimer: true,
          timeTaken: "2 mins",
        },
        LOCKED: {
          color: "#313131",
          message: "Upload your PAN and Selfie to verify your identity",
          showTimer: true,
          timeTaken: "2 mins",
        },
        icon: PendingKyc,
      },
      FAILED: {
        UNLOCKED: {
          color: "#FA4D4D",
          message: "Upload your PAN and Selfie to verify your identity",
          action: {
            text: "Retry",
            className: `${
              !isClicked ? "bg-white" : "bg-gray-500"
            } text-black text-2xs px-2 py-0.5 rounded-md flex gap-1 items-center`,
            icon: <RefreshCw size={12} />,
            onClick: () => {
              setIsClicked((prevState) => !prevState)
              clickKycAction()
            },
          },
          showTimer: true,
          timeTaken: "2 mins",
        },
        LOCKED: {
          color: "#FA4D4D",
          message: "Upload your PAN and Selfie to verify your identity",
          showTimer: true,
          timeTaken: "2 mins",
        },
        RETRY: {
          color: "#FA4D4D",
          message: null,
          action: {
            text: "Retry",
            className: `${
              !isClicked ? "bg-white" : "bg-gray-500"
            } text-black text-2xs px-2 py-0.5 rounded-md flex gap-1 items-center`,
            icon: <RefreshCw size={12} />,
            onClick: () => {
              setIsClicked((prevState) => !prevState)
              clickKycAction()
            },
          },
          statusMessage: {
            text: (
              <>
                <Frown className="w-4 h-4 mr-2" />
                Couldn't Approve
              </>
            ),
            className: "border-red-700 text-red-700",
          },
        },
        icon: RejectedKyc,
      },
      WAITING: {
        LOCKED: {
          color: "#FFE586",
          message: "Upload your PAN and Selfie to verify your identity",
          headingTextColor: "#000000", // Add this to set heading text color
          action: {
            text: "Support",
            className: "bg-white text-black text-2xs px-2 py-0.5 rounded-md flex gap-1 items-center mt-10",
            icon: null,
            onClick: () =>
              openSupportDialog(
                "kyc-stuck",
                "My KYC/identity verification is under review and I am still waiting.",
              ),
          },
        },
        icon: PendingKyc, // Keep existing icon
      },
      REJECTED: {
        UNLOCKED: {
          color: "#FA4D4D",
          message: "Upload your PAN and Selfie to verify your identity",
        },
        LOCKED: {
          color: "#FA4D4D",
          message: "Upload your PAN and Selfie to verify your identity",
        },
        icon: RejectedKyc,
      },
    },
    BANKING: {
      heading: "Income Verification",
      PENDING: {
        UNLOCKED: {
          color: "#0031CC",
          message: "Connect your salary bank account to verify your income",
          action: {
            text: "Start",
            className: "bg-white text-black text-2xs px-2 py-0.5 rounded-md flex gap-1 items-center",
            icon: (
              <div className="rounded-full bg-black">
                <ChevronRight color="#000" size={12} />
              </div>
            ),
            showTimer: true,
            onClick: () => navigate(`/banking`),
          },
          showTimer: true,
          timeTaken: "2 mins",
        },
        LOCKED: {
          color: "#313131",
          message: "Connect your salary bank account to verify your income",
          showTimer: true,
          timeTaken: "2 mins",
        },
        icon: PendingBanking,
      },
      FAILED: {
        UNLOCKED: {
          color: "#FA4D4D",
          message: "Please connect the bank account in which you get your monthly salary",
          action: {
            text: "Retry",
            className: "bg-white text-black text-2xs px-2 py-0.5 rounded-md flex gap-1 items-center",
            icon: <RefreshCw size={12} />,
            onClick: () => navigate(`/banking`),
          },
          showTimer: true,
          timeTaken: "2 mins",
        },
        LOCKED: {
          color: "#FA4D4D",
          message: "Please connect the bank account in which you get your monthly salary",
          showTimer: true,
          timeTaken: "2 mins",
        },
        RETRY: {
          color: "#FA4D4D",
          message: null,
          action: {
            text: "Retry",
            className: "bg-white text-black flex items-center",
            icon: <RefreshCw className="w-5 h-5 ml-2" />,
            showTimer: false,
          },
          statusMessage: {
            text: (
              <>
                <Frown className="w-4 h-4 mr-2" />
                Couldn't Approve
              </>
            ),
            className: "border-red-700 text-red-700",
          },
        },
        icon: RejectedBanking,
      },
      RETRY: {
        UNLOCKED: {
          color: "#FA4D4D",
          message: "Please connect the bank account in which you get your monthly salary",
          action: {
            text: "Retry",
            className: "bg-white text-black text-2xs px-2 py-0.5 rounded-md flex gap-1 items-center",
            icon: <RefreshCw size={12} />,
            onClick: () => navigate(`/banking`),
          },
          statusMessage: {
            text: (
              <>
                <Frown className="w-4 h-4 mr-2" />
                Couldn't Approve
              </>
            ),
            className: "border-red-700 text-red-700",
          },
          showTimer: true,
          timeTaken: "2 mins",
        },
        LOCKED: {
          color: "#FA4D4D",
          message: "Please connect the bank account in which you get your monthly salary",
          showTimer: true,
          timeTaken: "2 mins",
        },
        RETRY: {
          color: "#FA4D4D",
          message: null,
          action: {
            text: "Retry",
            className: "bg-white text-black flex items-center",
            icon: <RefreshCw className="w-5 h-5 ml-2" />,
            showTimer: false,
          },
          statusMessage: {
            text: (
              <>
                <Frown className="w-4 h-4 mr-2" />
                Couldn't Approve
              </>
            ),
            className: "border-red-700 text-red-700",
          },
        },
        icon: RejectedBanking,
      },
      WAITING: {
        LOCKED: {
          color: "#FFE586",
          message: "Upload your PAN and Selfie to verify your identity",
          headingTextColor: "#000000",
          action: {
            text: "Support",
            className: "bg-white text-black text-2xs px-2 py-0.5 rounded-md flex gap-1 items-center mt-10",
            icon: null,
            onClick: () =>
              openSupportDialog(
                "banking-stuck",
                "My income/bank verification is stuck and I am still waiting.",
              ),
          },
        },
        icon: ProgressBanking,
      },
      REJECTED: {
        UNLOCKED: {
          color: "#FA4D4D",
          message: "Please connect the bank account in which you get your monthly salary",
        },
        LOCKED: {
          color: "#FA4D4D",
          message: "Please connect the bank account in which you get your monthly salary",
        },
        icon: RejectedBanking,
      },
    },
    E_SIGN: {
      heading: "E-Sign/Sanction",
      PENDING: {
        UNLOCKED: {
          color: "#0031CC",
          message: "Check your rent summary and sign via simple OTP",
          action: {
            text: "Start",
            className: "bg-white text-black text-2xs px-2 py-0.5 rounded-md flex gap-1 items-center",
            icon: (
              <div className="rounded-full bg-black">
                <ChevronRight color="#000" size={12} />
              </div>
            ),
            onClick: () => startESign(),
          },
          showTimer: true,
          timeTaken: "2 mins",
        },
        LOCKED: {
          color: "#313131",
          message: "Check your rent summary and sign via simple OTP",
          showTimer: true,
          timeTaken: "2 mins",
        },
        icon: PendingEsign,
      },
      FAILED: {
        UNLOCKED: {
          color: "#FA4D4D",
          message: "Check your rent summary and sign via simple OTP",
          action: {
            text: "Retry",
            className: "bg-white text-black text-2xs px-2 py-0.5 rounded-md flex gap-1 items-center",
            icon: <RefreshCw size={12} />,
            onClick: () => startESign(),
          },
          showTimer: true,
          timeTaken: "2 mins",
        },
        LOCKED: {
          color: "#FA4D4D",
          message: "Check your rent summary and sign via simple OTP",
          showTimer: true,
          timeTaken: "2 mins",
        },
        RETRY: {
          color: "#FA4D4D",
          message: "Check your rent summary and sign via simple OTP",
          action: {
            text: "Retry",
            className: "bg-white text-black flex items-center",
            icon: <RefreshCw className="w-5 h-5 ml-2" />,
            showTimer: false,
          },
          statusMessage: {
            text: (
              <>
                <Frown className="w-4 h-4 mr-2" />
                Couldn't Approve
              </>
            ),
            className: "border-red-700 text-red-700",
          },
        },
        icon: RejectedESign,
      },
      WAITING: {
        LOCKED: {
          color: "#FFE586",
          message: "Upload your PAN and Selfie to verify your identity",
          headingTextColor: "#000000",
          action: {
            text: "Support",
            className: "bg-white text-black text-2xs px-2 py-0.5 rounded-md flex gap-1 items-center mt-10",
            icon: null,
            onClick: () =>
              openSupportDialog(
                "esign-stuck",
                "My e-sign/sanction step is stuck and I am still waiting.",
              ),
          },
        },
        icon: ProgressESign,
      },
      REJECTED: {
        UNLOCKED: {
          color: "#FA4D4D",
          message: "Please connect the bank account in which you get your monthly salary",
        },
        LOCKED: {
          color: "#FA4D4D",
          message: "Please connect the bank account in which you get your monthly salary",
        },
        icon: RejectedESign,
      },
    },
  }

  const stepStatus = data.status

  const headingBgColor = cardConfig[data.name]?.[data.status]?.[data.state]?.color || "#313131"
  const headingText = cardConfig[data.name]?.heading
  const subText = cardConfig[data.name]?.[data.status]?.[data.state]?.message
  const showTimer = cardConfig[data.name]?.[data.status]?.[data.state]?.showTimer
  const timeRequired = cardConfig[data.name]?.[data.status]?.[data.state]?.timeTaken || "2 mins"
  const actionButton = cardConfig[data.name]?.[data.status]?.[data.state]?.action
  const icon = cardConfig[data.name]?.[data.status]?.icon
  const headingTextColor = cardConfig[data.name]?.[data.status]?.[data.state]?.headingTextColor || "#eeeeee"

  const lockedActionButton = cardConfig[data.name]?.[data.status]?.[data.state]?.action

  const supportDialogNode = (
    <SupportRequestDialog
      open={supportDialog.open}
      onOpenChange={(open) => setSupportDialog((prev) => ({ ...prev, open }))}
      topic={supportDialog.topic}
      problemLine={supportDialog.problemLine}
    />
  )

  if (stepStatus === "SUCCESS") {
    return (
      <div className="w-full px-4">
        <div className="flex justify-between border border-[#13C06DC2] text-[#12c06c] items-center p-4 text-sm rounded-md">
          <span>{headingText}</span>
          <div className="flex items-center text-2xs px-1 py-1 gap-2 rounded-full border border-[#12c06c]">
            <CircleCheck size={12} color="#12c06c" />
            Completed
          </div>
        </div>
        {supportDialogNode}
      </div>
    )
  } else {
    return (
      <div className="w-full px-4">
        <div className="flex">
          <div className="w-[70%] flex items-center justify-center pr-2 pb-2 rounded-md">
            <div
              className="rounded-md w-full flex items-center justify-center p-2"
              style={{
                backgroundColor: headingBgColor,
                color: headingTextColor, // Apply the custom text color
              }}
            >
              {headingText}
            </div>
          </div>
          <div
            className="w-[30%] flex items-center justify-center pr-2 pb-2 rounded-t-md border-t border-l border-r"
            style={{ borderColor: headingBgColor }}
          >
            <img src={icon || "/placeholder.svg"} className="-mb-12 pt-4" alt="" />
          </div>
        </div>
        <div className="flex">
          <div
            className="flex flex-col gap-4 w-[70%] p-4 rounded-bl-md rounded-tl-md border-l border-t border-b"
            style={{ borderColor: headingBgColor }}
          >
            <div className="w-full text-[#555555] text-2xs font-medium">{subText}</div>
            {showTimer && (
              <div className="w-fit px-2 py-[0.5px] border border-[#555555] rounded-md flex items-center gap-2 text-[#555555]">
                <Timer size={12} />
                <span className="text-[#555555] text-3xs font-medium">{timeRequired}</span>
              </div>
            )}
          </div>
          <div
            className="flex flex-col justify-end items-center w-[30%] p-4 rounded-br-md border-b border-r"
            style={{ borderColor: headingBgColor }}
          >
            {lockedActionButton && (
              <button onClick={lockedActionButton.onClick} className={`${lockedActionButton.className}`}>
                {lockedActionButton.text}
              </button>
            )}
          </div>
        </div>
        {supportDialogNode}
      </div>
    )
  }
}
